import { getUserPath } from "@iluvatar/global/src/constants";
import { ISuccessResponse, UserEntity } from "@iluvatar/global/src/typings";
import { DeepPartial } from "@iluvatar/global/src/typings/ts-utils";
import type { NotificationKind } from "@iluvatar/global/src/typings/notifications";
import { queryClient } from "@/contexts/react-query/query-client";

import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";
import requestService from "@/services/request.service";

import { useDocSnap } from "./useSubscription.v2";

const userApi = {
  markNotificationsRead: function (input: {
    kind: NotificationKind;
    chatId?: string;
    through: number;
    notificationId: string;
  }) {
    return requestService
      .request("/api/notifications/read", {
        method: "post",
        body: JSON.stringify(input),
      })
      .then(async (response) => {
        if (input.kind === "messages") {
          await queryClient.invalidateQueries({
            queryKey: ["unread-message-count"],
          });
        }
        return response;
      });
  },
  disconnectMatch: function (
    uid: string,
    matchUserId: string,
    matchId: string,
  ) {
    return requestService.request(`/api/user/${uid}/disconnect`, {
      method: "post",
      body: JSON.stringify({ matchId, matchUserId }),
    });
  },
  updateUser: function (userId: string, user: DeepPartial<UserEntity>) {
    return requestService.request(`/api/user/${userId}`, {
      method: "put",
      body: JSON.stringify({ user }),
    });
  },
  updateUserImage: function (
    userId: string,
    communityId: string,
    imageId: string,
    isPrivate: boolean,
  ) {
    return requestService.request(
      `/api/user/${encodeURIComponent(userId)}/image/${encodeURIComponent(imageId)}`,
      {
        method: "put",
        body: JSON.stringify({ communityId, isPrivate }),
      },
    );
  },
  removeImage: function (
    userId: string,
    communityId: string,
    imageRef: string,
  ) {
    return requestService.request(`/api/user/${userId}/image/${imageRef}`, {
      method: "delete",
      body: JSON.stringify({ profileId: communityId }),
    });
  },
  likeUser: function (userId: string, communityId: string = "default") {
    return requestService.request<ISuccessResponse<boolean>>(
      `/api/user/${userId}/like`,
      {
        method: "post",
        body: JSON.stringify({ communityId }),
      },
    );
  },
  ignoreUser: function (userId: string, communityId: string = "default") {
    return requestService.request(`/api/user/${userId}/ignore`, {
      method: "post",
      body: JSON.stringify({ communityId }),
    });
  },
  search: function (communityId: string = "default", exclude: string[]) {
    return requestService.request("/api/search", {
      method: "post",
      body: JSON.stringify({ communityId, exclude: exclude }),
    });
  },
};

export function useCurrentUser() {
  const { user } = useAuthenticationContext();

  const [_user, state, error] = useDocSnap<UserEntity>(
    user ? getUserPath(user!.uid) : "",
    { enabled: Boolean(user && user.uid) },
  );

  return {
    user: _user,
    state: state,
    error: error,
    api: userApi,
  };
}
